import PropTypes from 'prop-types';
import React from 'react';

import useLoadingResources from '@glass/web/modules/resources/useLoadingResources';
import { CENTER } from '@glass/web/modules/theme/constants';
import { FLEX } from '@glass/web/modules/theme/flexConstants';
import makeStyles from '@glass/web/modules/theme/makeStyles';

import ContentTag from '@glass/shared/components/Content/ContentTag';
import ResourceLink from '@glass/shared/components/Link/ResourceLink';

const useStyles = makeStyles()((theme, { disablePadding, spacing, disableLeftPadding }) => ({
  root: {
    display: FLEX,
    flexWrap: 'nowrap',
    overflow: 'auto',
    alignItems: CENTER,
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    MsOverflowStyle: 'none',
    scrollbarWidth: 'none',
    padding: !disablePadding ? theme.spacing(spacing, 0) : undefined,
    '& > div > *': {
      marginRight: theme.spacing(spacing),
    },
  },
  container: {
    display: FLEX,
    marginLeft: disablePadding || disableLeftPadding ? undefined : theme.spacing(spacing),
  },
}));

function TagList({
  tags: tagsProp,
  size,
  loading,
  numLoading,
  disablePadding,
  disableLeftPadding,
  onTagClick,
  spacing,
}) {
  const { classes } = useStyles({ disablePadding, disableLeftPadding, spacing });
  const loadingTags = useLoadingResources(numLoading);
  const tags = loading && !tagsProp?.length ? loadingTags : tagsProp;

  if (!tags?.length) {
    return null;
  }

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        {(loading && loadingTags?.length ? loadingTags : tags)?.map?.(
          ({ name, slug, color: tagColor }) => (
            <ContentTag
              key={`content-tag-${slug}`}
              color={tagColor}
              component={!loading && slug ? ResourceLink : undefined}
              label={name}
              loading={loading}
              size={size}
              tag={!loading && slug ? slug : undefined}
              onClick={onTagClick}
            />
          ),
        )}
      </div>
    </div>
  );
}

TagList.propTypes = {
  disableLeftPadding: PropTypes.bool,
  disablePadding: PropTypes.bool,
  loading: PropTypes.bool,
  numLoading: PropTypes.number,
  onTagClick: PropTypes.func,
  size: PropTypes.string,
  spacing: PropTypes.number,
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      color: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    }),
  ),
};

TagList.defaultProps = {
  numLoading: 2,
  disablePadding: false,
  disableLeftPadding: false,
  spacing: 1,
};

TagList.displayName = 'TagList';

export default React.memo(TagList);
